import moment from "moment";

import momentTimeZone from "moment-timezone";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";
// Local imports
import { getHolidays } from "./holidays";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const HALF_DAY = 12;

function toDateDiff(d) {
	const date = new Date(d);
	//TODO: we should get the current day form an API

	const today = new Date(Date.now());
	date.setHours(0, 0, 0, 0);
	today.setHours(0, 0, 0, 0);

	const msDiff = today.getTime() - date.getTime();
	return Math.round(msDiff / (1000 * 60 * 60 * 24));
}

function dateDifference(date = moment(), modifier = "days", format = DATE_FORMAT.usDateDash) {
	const actualDate = moment();
	const dateToCompare = moment(date, format);
	return actualDate.diff(dateToCompare, modifier);
}

function getFormatDate(date, format = DATE_FORMAT.monthAbbrDatePadded) {
	let result = "";
	if (date) {
		try {
			const current = new Date(date);
			result = moment(current.getTime()).format(format);
		} catch (error) {
			console.log(error);
		}
	}
	return result;
}

/**
 * Returns a formatted UTC date.
 * @param {string | number | Date} date A valid date
 * @param {string} format A valid date format
 * @returns {string}
 */
function getFormatUTCDate(date, format = DATE_FORMAT.monthAbbrDatePadded) {
	let result = "";
	if (date) {
		try {
			const current = new Date(date);
			result = moment(current.getTime()).utc().format(format);
		} catch (error) {
			console.log(error);
		}
	}
	return result;
}

function toDateDiffDaysHoursMins(dateEntry) {
	let difference = moment(dateEntry).fromNow();

	if (difference === "a few seconds ago") {
		difference = "just now";
	}

	if (difference.includes("a ")) {
		difference = difference.replace("a ", "1 ");
	}
	if (difference.includes("an ")) {
		difference = difference.replace("an ", "1 ");
	}

	return difference;
}

const getDateDifference = (date) => {
	const start = moment(date);
	const end = moment();
	const TOTAL_MONTHS = 12;

	const monthsTotal = end.diff(start, "months", true) / TOTAL_MONTHS;
	const days = end.diff(start, "days");

	const [years, months] = monthsTotal.toFixed(1).split(".");

	return {
		years,
		months,
		days,
	};
};

const getRightLabel = (value, label) => `${value} ${label}${Number(value) > 1 ? "s" : ""}`.trim();

function getLongevity(startDate) {
	if (!startDate || !moment(startDate).isValid()) {
		return "No date provided.";
	}

	const { years, months, days } = getDateDifference(startDate);

	if (Number(years) > 0 && Number(months) < 1) {
		return getRightLabel(years, "year");
	} else if (Number(months) > 0 && Number(years) < 1) {
		return getRightLabel(months, "month");
	} else if (Number(months) < 1 && Number(years) < 1) {
		return getRightLabel(days, "day");
	}

	return `${getRightLabel(years, "year")} and ${getRightLabel(months, "month")}`;
}

function formatDate(date, endDate = false) {
	const [year, month, day] = date.split("-");
	return new Date(`${month}-${day.substring(0, 2)}-${year} ${endDate ? "23:59:59" : "00:00:00"}`);
}

// call this function, passing-in your date
function dateToFromNowDaily(myDate) {
	// get from-now for this date
	const fromNow = moment(myDate).fromNow();

	// ensure the date is displayed with today and yesterday
	return moment(myDate).calendar(null, {
		// when the date is closer, specify custom values
		lastWeek: "[Last] dddd",
		lastDay: "[Yesterday]",
		sameDay: "[Today]",
		nextDay: "[Tomorrow]",
		nextWeek: "dddd",
		// when the date is further away, use from-now functionality
		sameElse: () => `[${fromNow}]`,
	});
}

//returns only formatted time from date
function getTimeFromDate(date) {
	let time = String(date);
	time = time.split("T")[1];
	if (time.includes(".")) {
		time = time.split(".")[0];
	}
	time = time.split(":");
	while (time[0].length < 2) {
		time[0] = "0" + time[0];
	}
	while (time[1].length < 2) {
		time[1] = "0" + time[1];
	}

	const AM = parseInt(time[0], 10) < HALF_DAY;
	const meridian = AM ? "A.M." : "P.M.";
	return `${time[0]}:${time[1]} ${meridian}`;
}

const convertToTimeDisplay = (time) => {
	if (time === "-") {
		return time;
	}
	let timeString = String(time);
	let hours = 0;
	let minutes = 0;
	if (time === 0) {
		return "00h 00m";
	}
	if (!timeString.includes(".") && timeString !== "") {
		return timeString + "h 00m";
	} else {
		timeString = timeString.split(".");
		hours = parseInt(timeString[0], 10);
		minutes = timeString[1];
		while (minutes.length < 2) {
			minutes += "0";
		}
		minutes = parseInt(minutes, 10);
		if (minutes >= 1 && minutes <= HALF_DAY) {
			minutes = "00";
		} else if (minutes >= 13 && minutes <= 37) {
			minutes = "15";
		} else if (minutes >= 38 && minutes <= 62) {
			minutes = "30";
		} else if (minutes >= 63 && minutes <= 87) {
			minutes = "45";
		} else if (minutes >= 88) {
			minutes = "00";
			hours++;
		}
	}
	return `${hours}h ${minutes}m`;
};

const resolveDateTime = (date) => {
	if (typeof date === "object") {
		return date?.getTime?.();
	}
	return Date.parse(date);
};

/**
 * This functions is useful for commission cycle dates (start & end) because
 * it changes ONLY the UTC time to the local time keeping the same date and time.
 * E.x.:
 * 		"2024-11-01T00:00:00.000Z (UTC-0)" -> getDateOnlyChangingUTC -> "2024-11-01T00:00:00.000-04:00" (UTC-4)
 *
 * @param {date} string A valid date in UTC-0 (2024-11-01T00:00:00.000Z)
 * @returns {Moment} A moment object with the local time
 */
const getDateOnlyChangingUTC = (date) => {
	if (typeof date === "string") {
		// Gets the browser's local timezone (e.g., "America/New_York" or "Europe/London").
		const localTimezone = momentTimeZone.tz.guess();

		// Apply the localTimezone to the param date but neither change the date (YYYY-MM-DD) nor time (hh:mm:ss), only the UTC
		return moment(date).utc().tz(localTimezone, true);
	}

	return date;
};

function getMaxDate(dates, format = DATE_FORMAT.usDateTime12hPadded) {
	if (!dates.length) {
		return "";
	}

	return moment.max(dates).format(format);
}

const getLocalDate = (date, localFormat, timeZone) => getFormattedDate(date, timeZone, localFormat);

export {
	toDateDiff,
	dateDifference,
	getFormatDate,
	getFormatUTCDate,
	toDateDiffDaysHoursMins,
	getLongevity,
	formatDate,
	dateToFromNowDaily,
	getTimeFromDate,
	convertToTimeDisplay,
	resolveDateTime,
	getHolidays,
	getDateOnlyChangingUTC,
	getMaxDate,
	getLocalDate,
};
